import '../field/ui-field';
import '../curtains/ui-curtain';
import '../curtains/ui-curtains';
import { browser } from '../../global/browser';
import { UIElement } from '../ui-element';
import {
    deepFreeze,
    getLanguageForCountry,
    isPlainObject,
    typedKeys,
} from '../../global/helpers';
import { createElement, TimeInSeconds } from '../../global/ui-helpers';
import { TabIndex } from '../../global/keyboard';
import { UIModal } from '../modal/ui-modal';
import { UIViews } from '../views/ui-views';
import { Digest } from '../../global/digest';
import type {
    CookieConsentCategoryList,
    CookieConsentValue,
    CookieConsentValueWithVersion,
} from './ui-cookie-consent.type';
import { CookieConsentCategory } from './ui-cookie-consent.enum';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { Component } from '../../decorators/component.decorator';
import { UIDropdown } from '../dropdown/ui-dropdown';
import type { ElementConfig } from '../ui-element.type';
import { Labels } from '../../global/labels';
import type { StorageDriver } from './storage-driver.type';
import { getStorageDrivers } from './storage-drivers';
import type { TranslationLabels } from '../../types/labels.type';
import type { EventHandler } from '../../global/utils.type';
import { Country, LanguageLong } from '../../global/enums';
import type {
    CookieConsentWarningFunction,
    CookieConsentLanguageSwitcherFunction,
} from './ui-cookie-consent.type';
import styles from './ui-cookie-consent.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UICookieConsent
 * @element ui-cookie-consent
 * @classdesc Represents a class for <code>ui-cookie-consent</code> element.
 * @fires event:cookie-consent-changed
 * @property {number} [expiresDays=365] {@attr expires-days} Cookies consent expiration in days.
 * @property {boolean} [forceShow] {@attr force-show} Force display cookie consent.
 * @property {boolean} [forceHide] {@attr force-hide} Forces to hide cookie consent.
 * consent UI doesn't respect if consent is given or not.
 * @property {string} hrefPolicy {@attr href-policy} Link to page about cookie, where
 * are details described.
 * @property {string} hrefEntities {@attr href-entities} Link to page about Swedbank entities.
 * @property {string} hrefCookies {@attr href-cookies} Link to page about cookies.
 * @property {string} hrefSettings {@attr href-settings} Link to page change settings.
 * @property {string} hrefUsage {@attr href-usage} Link to cookie usage page.
 * @property {string} target {@attr target} Specifies were 'about-link' will be opened,
 * values are similar like for &lt;a> attribute.
 * @property {Country} [country="EE"] {@attr country} Sets the country
 * where consent is shown.
 * @property {Language} language {@attr language} Sets the language
 * of the cookie consent element.
 * @property {boolean} handleStorage {@attr handle-storage} Also deletes localStorage data
 * @property {string} consentVersion {@attr consent-version} Version of the current consent.
 * Verifies checks if a user has the same consent version as request in a component, nothing to do.
 * Otherwise, re-ask the user's consent again.
 * @property {string} prefix Prefix of the storage key
 * @property {CookieConsentCategoryList} whitelist Whitelist of the cookies and storage, set from the
 * external service. Only very basic with one COOKIE_CONSENT is provided by default.
 * @property {UIModal} container Shortcut to container div.
 * @property {HTMLAnchorElement} declineButton Shortcut to decline button.
 * @property {HTMLButtonElement} acceptButton Shortcut to accept all button.
 * @property {HTMLButtonElement} settingsButton Shortcut to cookie settings button.
 * @property {HTMLButtonElement} saveChoiceButton Shortcut to accept settings button.
 * @property {HTMLAnchorElement} entitiesLink Shortcut to a change link in info text.
 * @property {UIDropdown} langSelector Shortcut to change language selector.
 * @property {Function} warn Warning function for disallowed cookies is set from outside, can be set
 * outside.
 * @example
 * <ui-cookie-consent></ui-cookie-consent>
 */
@Component({
    tag: 'ui-cookie-consent',
    styles,
})
export class UICookieConsent extends UIElement {
    public _languageSwitcher!: (lang: LanguageLong) => void;

    public static readonly ConsentName = 'COOKIE_CONSENT';
    public static readonly Category = CookieConsentCategory;

    private _prefix!: string;
    private storageDrivers = new Set<StorageDriver>();
    private initialConsentValue: CookieConsentValueWithVersion = {
        [UICookieConsent.Category.Necessary]: 0,
        [UICookieConsent.Category.Analytical]: 0,
        [UICookieConsent.Category.Targeting]: 0,
        [UICookieConsent.Category.Functional]: 0,
    };

    private static _whitelist: CookieConsentCategoryList;
    private static _warn: CookieConsentWarningFunction = (x) => x;
    private static readonly AllowedCategory = 1;
    private static readonly DisallowedCategory = 0;

    @Attribute(Number) expiresDays = 365;
    @Attribute(Boolean) forceShow = false;
    @Attribute(Boolean) forceHide = false;
    @Attribute(String) hrefPolicy!: string;
    @Attribute(String) hrefSettings!: string;
    @Attribute(String) hrefEntities!: string;
    @Attribute(String) hrefCookies!: string;
    @Attribute(String) hrefUsage!: string;
    @Attribute(String) target!: string;
    @Attribute(String) country: Country = Country.Estonia;
    @Attribute(String) language!: LanguageLong | '';
    @Attribute(Boolean) handleStorage = false;
    @Attribute() consentVersion: string = '';

    @Child('.ui-cookie-consent__container') container!: UIModal;
    @Child('.ui-cookie-consent__decline-button')
    declineButton!: HTMLButtonElement;
    @Child('.ui-cookie-consent__accept-button')
    acceptButton!: HTMLButtonElement;
    @Child('.ui-cookie-consent__settings-button')
    settingsButton!: HTMLButtonElement;
    @Child('.ui-cookie-consent__save-choice-button')
    saveChoiceButton!: HTMLButtonElement;
    @Child('.ui-cookie-consent__lang') langSelector!: UIDropdown;
    @Child('.ui-cookie-consent__views') views!: UIViews;

    private acceptHandler!: EventHandler;
    private declineHandler!: EventHandler;
    private saveChoiceHandler!: EventHandler;
    private languageSelectHandler!: EventHandler;
    private windowLoadHandler!: EventHandler;
    private settingsHandler!: EventHandler;

    constructor() {
        super();
        // Add native localStorage and sessionStorage. In case more things are
        // required in the future, such as cache, RAM, etc.
        // Can be added the similar way. Just implementing interface.
        getStorageDrivers().forEach((driver: StorageDriver) =>
            this.addStorageDriver(driver)
        );
    }

    /**
     * Prefix for storage, an external client might use prefixes.
     * @type {string}
     */
    get prefix(): string {
        return this._prefix || '';
    }

    set prefix(prefix: string) {
        this._prefix = prefix;
    }

    /**
     * @type {Function}
     */
    get languageSwitcher(): CookieConsentLanguageSwitcherFunction {
        return this._languageSwitcher;
    }

    set languageSwitcher(fn: (lang: LanguageLong) => void) {
        this._languageSwitcher = fn;
    }

    /**
     * Warning function for disallowed cookies is set from outside.
     * @type {Function | undefined}
     */
    static get warn(): CookieConsentWarningFunction {
        return UICookieConsent._warn;
    }

    static set warn(fn: (...args: Array<string>) => void) {
        UICookieConsent._warn = fn;
    }

    /**
     * Adds a storage service to the consent.
     * @param {StorageDriver} driver
     */
    addStorageDriver(driver: StorageDriver): void {
        this.storageDrivers.add(driver);
    }

    /**
     * Removes a storage service to the consent. In case of success, deletion
     * returns removed driver and null if a driver not found.
     * @param {string} name
     * @returns {StorageDriver | null}
     */
    removeStorageDriver(name: string): StorageDriver | null {
        for (const driver of this.getStorageDrivers()) {
            if (driver.name === name) {
                this.storageDrivers.delete(driver);
                return driver;
            }
        }
        return null;
    }

    /**
     * Gets all storage drivers for the consent component.
     * @returns {Set<StorageDriver>}
     */
    getStorageDrivers(): Set<StorageDriver> {
        return this.storageDrivers;
    }

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-cookie-consent', {
            mainTitle: 'Cookies',
            settingsTitle: 'Cookie settings',
            mainInfo:
                '{entitiesLink} use own and third-party {cookiesLink} or similar technologies that are necessary for this website to function. There are also cookies you can choose to improve your experience:',
            secondaryInfo:
                'You may withdraw your consent at any time on our {usageLink}. More information is available in our {policyLink}.',
            settingsInfo:
                'You may withdraw your consent at any time on our {usageLink}. For more information, see our {policyLinkSettings}.',
            accept: 'Accept all cookies',
            decline: 'Accept only necessary',
            myCookies: 'Manage cookies',
            saveChoice: 'Save cookie settings',
            HELP_NECESSARY:
                'Necessary for the website to work smoothly and securely and cannot be deselected. Enable important functions such as login and language settings.',
            HELP_ANALYTICAL:
                'Help us understand how users use our site, which functions are popular, which features might be complicated. We use this data to improve our site. If decline, no statistics will be collected.',
            HELP_TARGETING:
                'Help to provide you with personalized information about our products and services. If declined personalized content will not be presented to you.',
            HELP_FUNCTIONAL:
                'Make your browsing experience smoother. Without them, you might have to re-enter, filter content repeatedly, scroll more and miss out on other useful features.',
            CATEGORY_NECESSARY: 'Necessary',
            CATEGORY_ANALYTICAL: 'Analytical',
            CATEGORY_TARGETING: 'Targeting',
            CATEGORY_FUNCTIONAL: 'Functional',
            COOKIE_ANALYTICAL: 'Analytical',
            COOKIE_TARGETING: 'Targeting',
            COOKIE_FUNCTIONAL: 'Functional',
            EE: 'Estonia',
            LV: 'Latvia',
            LT: 'Lithuania',
            entities: 'We',
            cookies: 'cookies',
            language: 'Language',
            usage: 'Cookies page',
            policyLinkLabel: 'Cookie Policy',
            policyLinkSettingsLabel: 'Cookie Policy',
        });
    }

    /**
     * Whitelist of all Swedbank's approved cookies. This can be overridden or
     * expanded from other services.
     * @returns {Readonly<CookieConsentCategoryList>}
     */
    static get whitelist(): Readonly<CookieConsentCategoryList> {
        this._whitelist ??= {
            NECESSARY: [this.ConsentName],
            ANALYTICAL: [],
            TARGETING: [],
            FUNCTIONAL: [],
        };
        return deepFreeze(this._whitelist);
    }

    static set whitelist(list: CookieConsentCategoryList) {
        this._whitelist = list;
    }

    /**
     * Deletes absolutely all cookies for the domain.
     */
    destroyAllCookies(): void {
        document.cookie.split(';').forEach(this.destroyCookieByName.bind(this));
    }

    /**
     * Withdraws with cookie consent, remove COOKIE_CONSENT and shows
     * cookie dialog again.
     */
    withdraw(): void {
        document.cookie = `${UICookieConsent.ConsentName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
        this.show();
    }

    /**
     * Show view with info view (first layer).
     */
    showInfoView(): void {
        this.open();
    }

    /**
     * Show view with options (second layer), ignoring first layer.
     */
    showOptionsView(): void {
        this.views.selectByIndex(1);
        this.open();
    }

    /**
     * Opens the cookie consent dialog.
     */
    open(): void {
        const containerRef = UICookieConsent.props.children?.container;
        if (isPlainObject(containerRef)) {
            const selector = containerRef.selector ?? containerRef;
            this.dispatchCustomEvent('modal-open', {
                selector,
            });
            this.container.focus();
        }
    }

    /**
     * Closes the cookie consent dialog.
     */
    close(): void {
        this.views.selectByIndex(0);
        this.container.close();
    }

    /**
     * Destroys all unwanted cookies.
     */
    destroyUnwantedCookies(): void {
        const whitelistedCookies = this.getWhitelisted();
        // 1. Destroy every cookie which isn't whitelisted categories.
        // 2. Destroy every cookie which isn't in whitelist at all.
        // But usually set manually or by 3rd parties.
        this.getAllCookiesList().forEach((cookieName) => {
            let isAllowedCookie = false;
            for (const whitelistedCookie of whitelistedCookies) {
                if (
                    cookieName.startsWith(
                        this.normalizePattern(whitelistedCookie)
                    )
                ) {
                    isAllowedCookie = true;
                    break;
                }
            }
            if (!isAllowedCookie) {
                this.destroyCookieByName(cookieName);
            }
            if (
                cookieName !== '' &&
                !this.isCookieWhitelisted(cookieName) &&
                typeof UICookieConsent.warn === 'function'
            ) {
                UICookieConsent.warn(
                    'COOKIES WARNING: %c' +
                        cookieName +
                        '%c is not approved by the Swedbank group or user ' +
                        'permission is not given. It has been automatically removed!',
                    'font-weight: bold;',
                    'font-weight: normal;'
                );
            }
        });
    }

    /**
     * Destroys all unwanted storage.
     */
    destroyUnwantedStorage(): void {
        if (!this.handleStorage) {
            return;
        }
        const whiteListedStorage = this.getWhitelisted();
        this.getStorageDrivers().forEach((driver) => {
            const storageList: Array<string> = [];
            for (let i = 0; i < driver.length(); ++i) {
                const keyValue = driver.key(i);
                if (keyValue !== null) {
                    storageList.push(keyValue);
                }
            }
            storageList.forEach((storageKey) => {
                let isStorageKeyAllowed = false;
                for (const storage of whiteListedStorage) {
                    if (
                        (this.prefix + storageKey).includes(storage) ||
                        storageKey.startsWith(this.normalizePattern(storage))
                    ) {
                        isStorageKeyAllowed = true;
                        break;
                    }
                }
                if (!isStorageKeyAllowed) {
                    driver.remove(storageKey);
                    if (typeof UICookieConsent.warn === 'function') {
                        UICookieConsent.warn(
                            'STORAGE WARNING: %c' +
                                storageKey +
                                '%c is not approved by the Swedbank group or user ' +
                                'permission is not given. It has been automatically removed!',
                            'font-weight: bold;',
                            'font-weight: normal;'
                        );
                    }
                }
            });
        });
    }

    /**
     * Get whitelisted cookies or storage.
     * @returns {string[]}
     */
    getWhitelisted(): Array<string> {
        return this.getAllowedCategories().flatMap(
            (category) => UICookieConsent.whitelist[category]
        );
    }

    /**
     * Get allowed categories by user.
     * @returns {CookieConsentCategory[]}
     */
    getAllowedCategories(): Array<CookieConsentCategory> {
        const selectedCategories = this.getSelectedCategories();
        const keys = typedKeys<CookieConsentValue>(selectedCategories);
        return keys.filter((category) => {
            return (
                UICookieConsent.AllowedCategory === selectedCategories[category]
            );
        });
    }

    /**
     * Destroys cookies by its name.
     * @param {string} cookieName
     */
    destroyCookieByName(cookieName: string): void {
        const params = [
            cookieName + '=',
            'expires=Thu, 01 Jan 1970 00:00:00 GMT',
        ];
        if (this.isSecureContext()) {
            params.push('Secure=true');
            params.push('SameSite=Strict');
        }
        params.push('path=/');
        const entry = params.join(';');
        // ^ Start of the line
        // [a-z0-9]+ one or more alphanumeric characters in lowercase
        // \. a dot
        // ? the entire sequence of alphanumeric characters followed by a dot is optional
        // $ End of the line
        const hostReplace = /^([a-z0-9]+\.)?/;
        document.cookie = entry;
        [
            location.hostname,
            location.hostname.replace(hostReplace, '.'),
        ].forEach((domain) => {
            document.cookie = entry + ';domain=' + domain;
        });
    }

    /**
     * Creates checkboxes for categories in 'settings' view.
     * @returns {HTMLUListElement}
     * @private
     */
    private buildCategoriesList(): HTMLUListElement {
        const consentValues = this.getSelectedCategories();
        return createElement<HTMLUListElement>(
            {
                tagName: 'div',
                classList: {
                    'input-list': true,
                },
                children: Object.values<CookieConsentCategory>(
                    UICookieConsent.Category
                ).map((category) => {
                    return {
                        tagName: 'div',
                        children: [
                            {
                                tagName: 'input',
                                classList: {
                                    'ui-cookie-consent__category-checkbox': true,
                                },
                                attributes: {
                                    name: category,
                                    id: `cookie-consent-category-${category}`,
                                    type: 'checkbox',
                                    disabled:
                                        category ===
                                        UICookieConsent.Category.Necessary,
                                    checked:
                                        category ===
                                            UICookieConsent.Category
                                                .Necessary ||
                                        consentValues[category] ===
                                            UICookieConsent.AllowedCategory,
                                },
                            },
                            {
                                tagName: 'label',
                                attributes: {
                                    for: `cookie-consent-category-${category}`,
                                },
                                children: [
                                    UICookieConsent.labels[
                                        `CATEGORY_${category}`
                                    ],
                                ],
                            },
                            {
                                tagName: 'p',
                                children: [
                                    UICookieConsent.labels['HELP_' + category],
                                ],
                            },
                        ],
                    };
                }),
            },
            true
        )!;
    }

    /**
     * Gets all selected settings.
     * @returns {CookieConsentValueWithVersion}
     */
    getSelectedCategories(): CookieConsentValueWithVersion {
        let json: CookieConsentValueWithVersion = {
            ...this.initialConsentValue,
        };
        const consent = this.getEncodedConsentValue();
        if (typeof consent === 'undefined') {
            return json;
        }
        try {
            json = JSON.parse(
                Digest.base64UnicodeDecode(consent)
            ) as CookieConsentValueWithVersion;
        } catch (e) {
            if (!this.container?.isActive()) {
                UICookieConsent.warn(
                    'Cannot parse consent value from cookies. Withdrawing consent.'
                );
                this.withdraw();
            }
        }
        return json;
    }

    /**
     * Returns the current version of an user consent.
     * @returns {string}
     */
    getConsentVersion(): string {
        return this.getSelectedCategories().VERSION ?? '';
    }

    /**
     * Gets the consent value extracted from document.cookie.
     * e.g., name=ABCDEF;maxAge=1000;domain=...
     * will extract ABCDEF.
     * Value decoded in base64.
     * @returns {string}
     */
    getConsentString(): string {
        return (
            document.cookie
                .split(';')
                .find((cookie) =>
                    cookie.includes(UICookieConsent.ConsentName)
                ) ?? ''
        );
    }

    /**
     * Create consent cookie in browser.
     * NB! There might be an issue for Chrome/Safari to set cookies on 'localhost'
     * without HTTPS enabled. If you have such an issue please use IP address
     * 127.0.0.1 or any other local IP address instead of 'http://localhost/'.
     * @param {string} value
     */
    createCookieConsent(value: string): void {
        const cookieParts = [
            `${UICookieConsent.ConsentName}=${Digest.base64UnicodeEncode(value)}`,
            'path=/',
            `domain=${browser.resolveMainDomain()}`,
            `max-age=${this.expiresDays * TimeInSeconds.Day}`,
        ];
        if (this.isSecureContext()) {
            cookieParts.push('Secure=true');
            cookieParts.push('SameSite=Strict');
        } else {
            cookieParts.push('SameSite=Lax');
        }
        document.cookie = cookieParts.join('; ');
    }

    /**
     * Checks that context of the page is secure (https).
     * @private
     * @returns {boolean}
     */
    private isSecureContext(): boolean {
        return browser.run(() => window.location.protocol === 'https:', false);
    }

    /**
     * Builds a policy link label with optional country suffix.
     * @param {boolean} useSettingsLabel - use policyLinkSettingsLabel instead of policyLinkLabel
     * @returns {string}
     * @private
     */
    private buildPolicyLinkLabel(
        useSettingsLabel: boolean
    ): TranslationLabels.Label {
        const labelKey = useSettingsLabel
            ? 'policyLinkSettingsLabel'
            : 'policyLinkLabel';
        return UICookieConsent.labels[labelKey];
    }

    /**
     * Creates a policy link element with href, target, and styling.
     * @param {string} label - the link label text
     * @returns {HTMLAnchorElement}
     * @private
     */
    private createPolicyLinkElement(
        label: TranslationLabels.Label
    ): HTMLAnchorElement {
        return createElement({
            tagName: 'a',
            attributes: {
                href: this.hrefPolicy || '#',
                target: this.target || null,
                rel: this.target === '_blank' ? 'noopener' : null,
            },
            classList: {
                'ui-cookie-consent__policy-link': true,
            },
            children: [label],
        })!;
    }

    /**
     * Creates about a cookie link with label and link.
     * @returns {HTMLAnchorElement}
     * @private
     */
    private createPolicyLink(): HTMLAnchorElement {
        const label = this.buildPolicyLinkLabel(false);
        return this.createPolicyLinkElement(label);
    }

    /**
     * Creates about a cookie link with label and link for cookie settings.
     * @returns {HTMLAnchorElement}
     * @private
     */
    private createPolicyLinkSettings(): HTMLAnchorElement {
        const label = this.buildPolicyLinkLabel(true);
        return this.createPolicyLinkElement(label);
    }

    /**
     * Creates change cookie link with label and link.
     * @returns {HTMLAnchorElement}
     * @private
     */
    private createEntitiesLink(): HTMLAnchorElement {
        return createElement({
            tagName: 'a',
            attributes: {
                href: this.hrefEntities || '#',
            },
            classList: {
                'ui-cookie-consent__entities-link': true,
            },
            children: [UICookieConsent.labels.entities],
        })!;
    }

    /**
     * Creates a cookie usage link with label and link.
     * @returns {HTMLAnchorElement}
     * @private
     */
    private createUsageLink(): HTMLAnchorElement {
        return createElement({
            tagName: 'a',
            attributes: {
                href: this.hrefUsage || '#',
            },
            classList: {
                'ui-cookie-consent__usage-link': true,
            },
            children: [UICookieConsent.labels.usage],
        })!;
    }

    /**
     * Creates a cookie link.
     * @returns {HTMLAnchorElement}
     * @private
     */
    private createCookieLink(): HTMLAnchorElement {
        return createElement({
            tagName: 'a',
            attributes: {
                href: this.hrefCookies || '#',
            },
            classList: {
                'ui-cookie-consent__cookie-link': true,
            },
            children: [UICookieConsent.labels.cookies],
        })!;
    }

    /**
     * Gets the correct country name based on attribute.
     * @returns {Label}
     * @private
     */
    private resolveCountryName(): TranslationLabels.Label {
        return UICookieConsent.labels[this.country];
    }

    /**
     * @param {string} valueCategory
     * @param {string} definedCategory
     * @returns {boolean}
     * @private
     */
    private compareCategories(
        valueCategory: string,
        definedCategory: string
    ): definedCategory is string {
        return valueCategory === definedCategory;
    }

    /**
     * Check if cookies has consent given.
     * @returns {boolean}
     */
    isConsentGiven(): boolean {
        return (
            document.cookie.includes(UICookieConsent.ConsentName) &&
            this.isConsentValid()
        );
    }

    /**
     * Checks if the cookie value of consent is a valid object. Also, If `VERSION` is
     * defined in `COOKIE_CONSENT` cookie and not equal to attribute `consent-version`
     * value, this means delete cookie consent and re-ask cookie.
     * @returns {boolean}
     */
    isConsentValid(): boolean {
        let result: boolean;
        try {
            const jsonString = Digest.base64UnicodeDecode(
                this.getEncodedConsentValue()
            );
            JSON.parse(jsonString);
            // This will allow resetting cookies, by changing the 'consent-version' attribute.
            result = this.getConsentVersion() === this.consentVersion;
        } catch {
            result = false;
        }
        if (!result) {
            this.destroyCookieByName(UICookieConsent.ConsentName);
        }
        return result;
    }

    /**
     * NOTE base64 might contain '=' equal sign at the end.
     * So we are interested only in value with index 1 (2nd element).
     * possible values are:
     *  - name=BASE64ABCDEDF==
     *  - name=BASE64ABCDEDF=
     *  - name=BASE64ABCDEDF
     * We are always interested in 2nd part just after '=' BASE64ABCDEDF.
     * @returns {string}
     * @private
     */
    private getEncodedConsentValue(): string {
        return this.getConsentString().split('=')[1];
    }

    /**
     * Saves cookie consent values.
     * @fires event:cookie-consent-changed
     */
    save(): void {
        const consentValue = this.buildConsentValuesFromCheckboxes();
        consentValue.VERSION = this.consentVersion;
        const values = JSON.stringify(consentValue);
        this.createCookieConsent(values);

        this.destroyUnwantedCookies();
        this.destroyUnwantedStorage();
        this.close();
        this.dispatchCustomEvent('cookie-consent-changed', values);
    }

    /**
     * Builds string for cookies.
     * @returns {CookieConsentValue}
     */
    buildConsentValuesFromCheckboxes(): CookieConsentValueWithVersion {
        const checkboxes = this.querySelectorAll<HTMLInputElement>(
            '.ui-cookie-consent__category-checkbox'
        );
        return [...checkboxes].reduce(
            (
                prev: CookieConsentValueWithVersion,
                current: HTMLInputElement
            ) => {
                const category = current.name as CookieConsentCategory;
                prev[category] = current.checked
                    ? UICookieConsent.AllowedCategory
                    : UICookieConsent.DisallowedCategory;
                return prev;
            },
            this.initialConsentValue
        );
    }

    /**
     * Handles accept all action
     * @private
     */
    private handleAccept(): void {
        this.selectAllCategories();
        this.save();
    }

    /**
     * Handles save choice selected settings action.
     * @private
     */
    private handleSaveChoice(): void {
        this.save();
    }

    /**
     * Handles decline selected settings action.
     * @private
     */
    private handleDecline(): void {
        this.deselectAdditionalCategories();
        this.save();
    }

    /**
     * Handles cookie settings action.
     * @private
     */
    private handleSettings(): void {
        this.views.selectByIndex(1);
    }

    /**
     * Selects all categories checkboxes.
     * @private
     */
    private selectAllCategories(): void {
        this.querySelectorAll<HTMLInputElement>(
            '.ui-cookie-consent__category-checkbox'
        ).forEach((checkbox) => {
            checkbox.checked = true;
        });
    }

    /**
     * Deselects all category checkboxes.
     * @private
     */
    private deselectAdditionalCategories(): void {
        [].filter
            .call(
                this.querySelectorAll<HTMLInputElement>(
                    '.ui-cookie-consent__category-checkbox'
                ),
                (checkbox: HTMLInputElement) => {
                    return !this.compareCategories(
                        checkbox.name,
                        UICookieConsent.Category.Necessary
                    );
                }
            )
            .forEach(
                (checkbox: HTMLInputElement) => (checkbox.checked = false)
            );
    }

    /**
     * Checks if cookie is whitelisted and allowed.
     * @param {string} cookieName
     * @returns {boolean}
     */
    isCookieWhitelisted(cookieName: string): boolean {
        const keys = typedKeys<typeof UICookieConsent.whitelist>(
            UICookieConsent.whitelist
        );
        const found = keys.reduce<Array<string>>((acc, category) => {
            const categories = UICookieConsent.whitelist[category].filter(
                (allowedCookiePattern) => {
                    return cookieName.startsWith(
                        this.normalizePattern(allowedCookiePattern)
                    );
                }
            );
            return acc.concat(categories);
        }, []);
        if (found.length === 0) {
            return false;
        }
        return cookieName.startsWith(this.normalizePattern(found[0]));
    }

    /**
     * Normalized pattern to match wildcard.
     * @param {string} pattern
     * @returns {string}
     * @private
     */
    private normalizePattern(pattern: string): string {
        return pattern.replace(/\*+$/, '');
    }

    /**
     * Gets all cookies for current domain
     * @returns {string[]}
     */
    getAllCookiesList(): Array<string> {
        return document.cookie.split(';').map((c) => c.trim().split('=')[0]);
    }

    /**
     * Prepare placeholders with links and appropriate labels.
     *
     * Note! {{days}} aren't used anymore, but it might again change in the future.
     * @returns {Record<string, string>}
     * @private
     */
    private getTextSubstitutions(): Record<
        string,
        string | TranslationLabels.Label
    > {
        return {
            country: this.resolveCountryName(),
            days: String(this.expiresDays),
            cookiesLink: this.hrefCookies
                ? this.createCookieLink().outerHTML
                : this.createCookieLink().innerHTML,
            policyLink: this.createPolicyLink().outerHTML,
            entitiesLink: this.createEntitiesLink().outerHTML,
            usageLink: this.createUsageLink().outerHTML,
            policyLinkSettings: this.createPolicyLinkSettings().outerHTML,
        };
    }

    /**
     * Create language selector for ui-cookie-consent.
     * @returns {ElementConfig}
     * @private
     */
    createLanguageSelector(): ElementConfig {
        const languages = getLanguageForCountry(this.country);
        const currentLang = this.language;
        return {
            tagName: 'ui-dropdown',
            classList: {
                'ui-cookie-consent__lang': true,
                '-hidden': !this.language,
            },
            attributes: {
                'layout': 'lang',
                'mobile-native': true,
                'hide-selected': true,
            },
            children: [
                {
                    tagName: 'select',
                    attributes: {
                        'aria-label': UICookieConsent.labels.language,
                    },
                    children: languages.short.map((_, i) => {
                        return {
                            tagName: 'option',
                            attributes: {
                                value: languages.long[i],
                                selected: languages.long[i] === currentLang,
                            },
                            children: [languages.short[i]],
                        };
                    }),
                },
            ],
        };
    }

    /**
     * Handles language switch.
     * @param {Event} event
     * @throws {Error}
     */
    handleLanguageSwitch(event?: Event): void {
        if (!event) {
            throw new Error('language change event is not set');
        }
        if (typeof this.languageSwitcher === 'function') {
            // If the language stays empty, the service using this component, itself
            // will fall back to default language.
            let language: LanguageLong = LanguageLong.Empty;
            if (
                event.target instanceof UIDropdown ||
                event.target instanceof HTMLSelectElement
            ) {
                language = event.target.value as LanguageLong;
            }
            this.languageSwitcher(language);
        } else {
            UICookieConsent.warn(
                "UICookieConsent.languageSwitcher function isn't defined"
            );
        }
    }

    /**
     * @private
     */
    private subscribeEvents(): void {
        this.acceptButton.addEventListener('click', this.acceptHandler);
        this.declineButton.addEventListener('click', this.declineHandler);
        this.settingsButton.addEventListener('click', this.settingsHandler);
        this.saveChoiceButton.addEventListener('click', this.saveChoiceHandler);
        this.langSelector.addEventListener(
            'change',
            this.languageSelectHandler
        );
        window.addEventListener('load', this.windowLoadHandler);
    }

    /**
     * @private
     */
    private unsubscribeEvents(): void {
        this.acceptButton.removeEventListener('click', this.acceptHandler);
        this.declineButton.removeEventListener('click', this.declineHandler);
        this.settingsButton.removeEventListener('click', this.settingsHandler);
        this.saveChoiceButton.removeEventListener(
            'click',
            this.saveChoiceHandler
        );
        this.langSelector.removeEventListener(
            'change',
            this.languageSelectHandler
        );
        window.removeEventListener('load', this.windowLoadHandler);
    }

    /**
     * @inheritDoc
     */
    render(): void {
        if (!this.country) {
            UICookieConsent.warn(
                'The country attribute is mandatory for ui-cookie-consent'
            );
            UICookieConsent.warn('EE, LV or LT should be set to country');
        }

        const substitutions = this.getTextSubstitutions();

        this.insertElements(
            [
                {
                    tagName: 'ui-modal',
                    attributes: {
                        compact: true,
                        noclosable: true,
                        tabindex: TabIndex.Active,
                        layout: UIModal.Layouts.Default,
                    },
                    classList: {
                        'ui-cookie-consent__container': true,
                    },
                    children: [
                        {
                            tagName: 'div',
                            classList: {
                                'ui-cookie-consent__inner': true,
                            },
                            children: [
                                this.createLanguageSelector(),
                                {
                                    tagName: 'ui-views',
                                    classList: {
                                        'ui-cookie-consent__views': true,
                                    },
                                    children: [
                                        {
                                            tagName: 'ui-view',
                                            children: [
                                                {
                                                    tagName: 'h2',
                                                    classList: {
                                                        'ui-cookie-consent__title': true,
                                                    },
                                                    children: [
                                                        this.getLabel(
                                                            'mainTitle',
                                                            substitutions
                                                        ),
                                                    ],
                                                },
                                                {
                                                    tagName: 'p',
                                                    children: [
                                                        this.getLabel(
                                                            'mainInfo',
                                                            substitutions
                                                        ),
                                                    ],
                                                },
                                                {
                                                    tagName: 'ul',
                                                    classList: {
                                                        'regular-list': true,
                                                    },
                                                    children: [
                                                        {
                                                            tagName: 'li',
                                                            children: [
                                                                this.getLabel(
                                                                    'COOKIE_FUNCTIONAL',
                                                                    substitutions
                                                                ),
                                                            ],
                                                        },
                                                        {
                                                            tagName: 'li',
                                                            children: [
                                                                this.getLabel(
                                                                    'COOKIE_ANALYTICAL',
                                                                    substitutions
                                                                ),
                                                            ],
                                                        },
                                                        {
                                                            tagName: 'li',
                                                            children: [
                                                                this.getLabel(
                                                                    'COOKIE_TARGETING',
                                                                    substitutions
                                                                ),
                                                            ],
                                                        },
                                                    ],
                                                },
                                                {
                                                    tagName: 'p',
                                                    children: [
                                                        this.getLabel(
                                                            'secondaryInfo',
                                                            substitutions
                                                        ),
                                                    ],
                                                },
                                                {
                                                    tagName: 'div',
                                                    classList: {
                                                        'ui-cookie-consent__controls': true,
                                                    },
                                                    children: [
                                                        {
                                                            tagName: 'button',
                                                            attributes: {
                                                                type: 'button',
                                                            },
                                                            classList: {
                                                                'button': true,
                                                                'ui-cookie-consent__accept-button': true,
                                                                '-guiding': true,
                                                            },
                                                            children: [
                                                                UICookieConsent
                                                                    .labels
                                                                    .accept,
                                                            ],
                                                        },
                                                        {
                                                            tagName: 'button',
                                                            attributes: {
                                                                type: 'button',
                                                            },
                                                            classList: {
                                                                'ui-cookie-consent__decline-button': true,
                                                                'button': true,
                                                                '-guiding': true,
                                                            },
                                                            children: [
                                                                UICookieConsent
                                                                    .labels
                                                                    .decline,
                                                            ],
                                                        },
                                                        {
                                                            tagName: 'button',
                                                            attributes: {
                                                                type: 'button',
                                                            },
                                                            classList: {
                                                                'ui-cookie-consent__settings-button': true,
                                                                'button': true,
                                                                '-guiding': true,
                                                            },
                                                            children: [
                                                                UICookieConsent
                                                                    .labels
                                                                    .myCookies,
                                                            ],
                                                        },
                                                    ],
                                                },
                                            ],
                                        },
                                        {
                                            tagName: 'ui-view',
                                            children: [
                                                {
                                                    tagName: 'h2',
                                                    classList: {
                                                        'ui-cookie-consent__title': true,
                                                    },
                                                    children: [
                                                        this.getLabel(
                                                            'settingsTitle',
                                                            substitutions
                                                        ),
                                                    ],
                                                },
                                                {
                                                    tagName: 'p',
                                                    children: [
                                                        this.getLabel(
                                                            'settingsInfo',
                                                            substitutions
                                                        ),
                                                    ],
                                                },
                                                {
                                                    tagName: 'ui-field',
                                                    attributes: {
                                                        layout: 'fluid',
                                                    },
                                                    children: [
                                                        this.buildCategoriesList(),
                                                    ],
                                                },
                                                {
                                                    tagName: 'div',
                                                    classList: {
                                                        'ui-cookie-consent__controls': true,
                                                    },
                                                    children: [
                                                        {
                                                            tagName: 'button',
                                                            attributes: {
                                                                type: 'button',
                                                            },
                                                            classList: {
                                                                'button': true,
                                                                'ui-cookie-consent__save-choice-button': true,
                                                            },
                                                            children: [
                                                                UICookieConsent
                                                                    .labels
                                                                    .saveChoice,
                                                            ],
                                                        },
                                                    ],
                                                },
                                            ],
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
            ],
            'beforeend',
            true
        );

        if (this.forceShow) {
            this.open();
            return;
        }

        if (this.isConsentGiven() || this.forceHide) {
            if (this.container.isActive()) {
                this.close();
            }
        } else {
            this.open();
        }
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        this.acceptHandler = this.handleAccept.bind(this);
        this.declineHandler = this.handleDecline.bind(this);
        this.saveChoiceHandler = this.handleSaveChoice.bind(this);
        this.languageSelectHandler = this.handleLanguageSwitch.bind(this);
        this.settingsHandler = this.handleSettings.bind(this);

        // Some cookies set on unique images on ibank, so cleanup again on window.load.
        this.windowLoadHandler = () => {
            this.destroyUnwantedCookies();
            this.destroyUnwantedStorage();
        };
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    reconnect(): void {
        this.subscribeEvents();
    }
}
